import {useEffect, useMemo, useRef, useState} from 'react';

type Item = {
    callback: Callback,
    once: boolean,
};

type Subscription = {
    subscribed: () => boolean,
    unsubscribe: () => void,
};

export type Callback = (entry: IntersectionObserverEntry) => void;
export type Options = IntersectionObserverInit & {
    enabled?: boolean,
    once?: boolean,
};

export default function useIntersectionObserver(
    {enabled = true, once: globalOnce = false, ...options}: Options = {}
) {
    const [observer, setObserver] = useState<IntersectionObserver | undefined>();

    const callbacks = useRef(
        new WeakMap<Element, Item>()
    );

    useEffect(() => {
        if (!enabled || window && !('IntersectionObserver' in window)) {
            return;
        }

        const instance = new IntersectionObserver((entries, observer) => {
            const list = callbacks.current;
            entries.forEach(entry => {
                if (!list.has(entry.target)) {
                    return;
                }

                const {callback, once} = list.get(entry.target) ?? {};
                try {
                    if (entry.isIntersecting && typeof callback === 'function') {
                        callback(entry);
                    }
                } finally {
                    if (once && entry.isIntersecting) {
                        list.delete(entry.target);
                        observer.unobserve(entry.target);
                    }
                }
            });
        }, {
            root: options.root,
            rootMargin: options.rootMargin,
            threshold: options.threshold,
        });
        setObserver(instance);

        return () => {
            instance?.disconnect();
            setObserver(undefined);
        };
    }, [callbacks, enabled, options.root, options.rootMargin, options.threshold]);

    return useMemo(() => {
        return {
            observer: observer,
            observe: (element: Element) => {
                observer?.observe(element);

                return {
                    unobserve: () => {
                        observer?.unobserve(element);
                    }
                };
            },
            unobserve: (element: Element): void => {
                observer?.unobserve(element);
            },
            subscribe: (element: Element, callback: Callback, once = globalOnce): Subscription => {
                callbacks.current?.set(element, {callback, once});

                return {
                    subscribed: (): boolean => callbacks.current?.has(element) === true,
                    unsubscribe: (): void => {
                        callbacks.current?.delete(element);
                    }
                };
            },
        };
    }, [globalOnce, observer]);
}
